import { useEffect, useState } from "react";
import { taxApi, insightsApi } from "../api.js";
import { Card, ErrorBanner, Spinner, EmptyState, MetricCard, Money, PageHeader } from "../components/ui.js";

interface TaxRuleDto {
  ruleId: string;
  jurisdiction: string;
  title: string;
  availability: string;
  authoritativeSource: string | null;
  sourceUrl: string | null;
  sourceCitation: string | null;
  ruleVersion: string | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  notes: string;
}

interface TaxSummaryDto {
  countryCode: string;
  taxPeriodId: string;
  periodStart: string;
  periodEnd: string;
  totalIncomeReceived: number;
  totalDeductibleExpenses: number;
  profitBeforeTax: number;
  estimatedIncomeTax: number | null;
  gstPayable: number | null;
  availability: string;
}

export function safeTaxSourceUrl(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:" ? url.href : null;
  } catch {
    return null;
  }
}

export function TaxPage() {
  const [summary, setSummary] = useState<TaxSummaryDto | null>(null);
  const [rules, setRules] = useState<TaxRuleDto[] | null>(null);
  const [rulesError, setRulesError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const [sumRes, rulesRes] = await Promise.all([taxApi.summary(), taxApi.rules()]);
      if (!sumRes.ok) setError(sumRes.error?.errorCode ?? "Tax summary unavailable");
      else setSummary(sumRes.data as TaxSummaryDto ?? null);
      if (!rulesRes.ok) setRulesError(rulesRes.error?.errorCode ?? "Tax rules unavailable");
      else setRules(rulesRes.data as TaxRuleDto[] ?? []);
    })();
  }, []);

  if (error) return <ErrorBanner message={error} />;
  if (!summary) return <Spinner />;

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Position"
        title="Tax"
        description="Review the tax information and verified rules currently available for your portfolio."
      />
      <div className="metric-grid">
        <MetricCard label="Income received" value={<Money amount={summary.totalIncomeReceived} currency={summary.countryCode === "NZ" ? "NZD" : undefined} />} hint={summary.taxPeriodId} />
        <MetricCard label="Deductible expenses" value={<Money amount={summary.totalDeductibleExpenses} currency={summary.countryCode === "NZ" ? "NZD" : undefined} />} hint="Based on recorded tax facts" />
        <MetricCard label="Profit before tax" value={<Money amount={summary.profitBeforeTax} currency={summary.countryCode === "NZ" ? "NZD" : undefined} />} hint={`Calculation availability: ${summary.availability}`} tone={summary.profitBeforeTax >= 0 ? "positive" : "attention"} />
      </div>
      <Card title="Tax position" description="Nettstay shows unavailable values explicitly rather than estimating without verified rules.">
        <dl className="summary-grid">
          <dt>Jurisdiction</dt><dd>{summary.countryCode}</dd>
          <dt>Tax period</dt><dd>{summary.taxPeriodId}</dd>
          <dt>Period start</dt><dd>{summary.periodStart.slice(0, 10)}</dd>
          <dt>Period end</dt><dd>{summary.periodEnd.slice(0, 10)}</dd>
          <dt>Estimated income tax</dt><dd>{summary.estimatedIncomeTax === null ? <span className="muted">Not available</span> : <Money amount={summary.estimatedIncomeTax} currency="NZD" />}</dd>
          <dt>GST payable</dt><dd>{summary.gstPayable === null ? <span className="muted">Not available</span> : <Money amount={summary.gstPayable} currency="NZD" />}</dd>
        </dl>
      </Card>
      <Card title="Applicable tax rules" description="Source and version details are retained so each rule can be traced.">
        {rulesError ? <ErrorBanner message={rulesError} /> : !rules ? <Spinner /> : rules.length === 0 ? <EmptyState title="No verified tax rules available" message="Nettstay will not guess when a verified rule is unavailable." /> : (
          <ul className="rules-list">
            {rules.map((r) => {
              const sourceUrl = safeTaxSourceUrl(r.sourceUrl);
              return <li key={r.ruleId}>
                <div className="rule-heading">
                  <strong>{r.title}</strong>
                  <span className="badge">{r.availability}</span>
                </div>
                <p className="muted">{r.authoritativeSource ?? "Authoritative source pending verification"}</p>
                {r.sourceCitation && <p className="small">{r.sourceCitation}</p>}
                <p className="muted small">
                  {r.ruleVersion ? `Version ${r.ruleVersion}` : "Version unavailable"}
                  {r.effectiveFrom ? ` · Effective ${r.effectiveFrom.slice(0, 10)}` : ""}
                </p>
                {sourceUrl && <a href={sourceUrl} target="_blank" rel="noreferrer">View source</a>}
              </li>;
            })}
          </ul>
        )}
        <p className="muted small">Tax calculations use only verified NZ Inland Revenue rules. Unverifiable rules are marked unavailable, not guessed.</p>
      </Card>
      <Card title="Anomaly review" description="Automated review remains transparent about whether it is enabled.">
        <AnomalyCheck />
      </Card>
    </div>
  );
}

function AnomalyCheck() {
  const [data, setData] = useState<{ aiEnabled: boolean; anomalies: unknown[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void insightsApi.anomalies().then((result) => {
      if (!result.ok) {
        setError(result.error?.errorCode ?? "Anomaly review unavailable");
        return;
      }
      setData(result.data as { aiEnabled: boolean; anomalies: unknown[] } | null);
    });
  }, []);
  if (error) return <ErrorBanner message={error} />;
  if (!data) return <Spinner />;
  return <p>AI anomaly detection: <strong>{data.aiEnabled ? "enabled" : "disabled"}</strong>. {data.anomalies.length} anomaly(ies) detected.</p>;
}
